import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getPushSubscriptionEndpoint, subscribeToPush, unsubscribeFromPush } from '../../../Infrastructure/Service/PushService';
import { ihm_viewer__dictionary, locale } from '../../../Application/i18n/i18nViewer';
import './NotificationBell.css';

const NotificationBell = ({ isAuthenticated = true, onLoginRequest = () => {} }) => {
    const [notifications, setNotifications] = useState([]);
    const [guestInfo, setGuestInfo] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [pushPermission, setPushPermission] = useState(window.Notification ? window.Notification.permission : 'default');
    const [notificationsEnabled, setNotificationsEnabled] = useState(
        localStorage.getItem('notifications_enabled') !== 'false'
    );
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchNotifications = async () => {
        if (!isAuthenticated) return;
        try {
            const resAuth = await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + '/api/notifications', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (resAuth.ok) {
                const data = await resAuth.json();
                setNotifications(data);
            }
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        }
    };

    const fetchGuestStatus = async () => {
        try {
            const endpoint = await getPushSubscriptionEndpoint();
            if (!endpoint) return;

            const res = await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + '/api/notifications/guest-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ endpoint })
            });
            if (res.ok) {
                const data = await res.json();
                setGuestInfo(data);
            }
        } catch (error) {
            console.error('Failed to fetch guest status', error);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 60000);
            return () => clearInterval(interval);
        } else {
            fetchGuestStatus();
            const interval = setInterval(fetchGuestStatus, 60000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'NEW_NOTIFICATION') {
                if (isAuthenticated) fetchNotifications();
                else fetchGuestStatus();
            }
        };
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleMessage);
            return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                if (event.target.closest('.notification-dropdown')) {
                    return;
                }
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef]);

    useEffect(() => {
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'notifications' }).then((status) => {
                status.onchange = () => {
                    setPushPermission(window.Notification.permission);
                    if (window.Notification.permission === 'granted' && localStorage.getItem('notifications_enabled') !== 'false') {
                        subscribeToPush();
                    }
                };
            });
        }
    }, []);

    const markAsRead = async (id, url) => {
        try {
            setNotifications(notifications.map(n => n.id === id ? { ...n, isRead: true } : n));
            await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + `/api/notifications/${id}/read`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
        } catch (e) {
            console.error('Error marking as read', e);
        } finally {
            if (url) {
                if (isMobile) {
                    setIsOpen(false);
                    window.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            }
        }
    };

    const markAllAsRead = async () => {
        try {
            await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + `/api/notifications/read/all`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            setNotifications(notifications.map(n => ({ ...n, isRead: true })));
        } catch (e) {
            console.error('Error marking all as read', e);
        }
    };

    const deleteNotification = async (e, id) => {
        e.stopPropagation();
        try {
            await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + `/api/notifications/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            setNotifications(notifications.filter(n => n.id !== id));
        } catch (e) {
            console.error('Error deleting notification', e);
        }
    };

    const deleteAll = async () => {
        try {
            await fetch((process.env.REACT_APP_U2_ACCOUNTSVC_LOGIN_URL || '') + '/api/notifications', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            setNotifications([]);
        } catch (e) {
            console.error('Error deleting all notifications', e);
        }
    };

    const requestPushPermission = async () => {
        if (window.Notification) {
            const permission = await window.Notification.requestPermission();
            setPushPermission(permission);
            if (permission === 'granted' && localStorage.getItem('notifications_enabled') !== 'false') {
                subscribeToPush();
            }
        }
    };

    const handleToggleNotifications = async () => {
        const newValue = !notificationsEnabled;
        setNotificationsEnabled(newValue);
        localStorage.setItem('notifications_enabled', newValue ? 'true' : 'false');
        
        if (newValue) {
            if (window.Notification && window.Notification.permission === 'default') {
                const permission = await window.Notification.requestPermission();
                setPushPermission(permission);
                if (permission === 'granted') {
                    subscribeToPush();
                }
            } else if (window.Notification && window.Notification.permission === 'granted') {
                subscribeToPush();
            }
        } else {
            await unsubscribeFromPush();
        }
    };

    const unreadCount = isAuthenticated ? notifications.filter(n => !n.isRead).length : (guestInfo?.unreadCount || 0);

    return (
        <div className="notification-bell-wrapper" ref={wrapperRef}>
            <div className="notification-bell-icon" onClick={() => {
                if (!isOpen) {
                    if (isAuthenticated) fetchNotifications();
                    else fetchGuestStatus();
                }
                setIsOpen(!isOpen);
            }}>
                {(notificationsEnabled && pushPermission === 'granted') ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <mask id="bell-slash-mask-main">
                                <rect x="0" y="0" width="24" height="24" fill="white" />
                                <line x1="3" y1="3" x2="21" y2="21" stroke="black" strokeWidth="3" strokeLinecap="round" />
                            </mask>
                        </defs>
                        <path mask="url(#bell-slash-mask-main)" d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                        <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                )}
                {isAuthenticated && unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </div>

            {isOpen && (isMobile ? createPortal(
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h4>{isAuthenticated ? ihm_viewer__dictionary.NOTIFICATIONS[locale()] : 'Notifications'}</h4>
                        <button className="close-mobile-btn" onClick={() => setIsOpen(false)} aria-label="Fermer">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                            </svg>
                        </button>
                    </div>

                    {!isAuthenticated ? (
                        <div className="notification-body">
                            <div className="notification-guest">
                                {guestInfo && guestInfo.linked && notificationsEnabled ? (
                                    <p>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_1[locale()]} <strong>{guestInfo.email}</strong>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_2[locale()]} <strong>{guestInfo.unreadCount}</strong>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_3[locale()]}
                                    </p>
                                ) : (
                                    <p>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_TEXT[locale()]}
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="notification-body">
                                {pushPermission !== 'granted' && (
                                    <div className="notification-push-promo">
                                        <p>
                                            {pushPermission === 'denied' 
                                                ? ihm_viewer__dictionary.NOTIFICATION_BLOCKED[locale()]
                                                : ihm_viewer__dictionary.NOTIFICATION_PROMO[locale()]}
                                        </p>
                                        {pushPermission === 'default' && (
                                            <button className="glass-btn" onClick={requestPushPermission} style={{ flexShrink: 0 }}>
                                                {ihm_viewer__dictionary.ACTIVATE[locale()]}
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div className="notification-list">
                                    {notifications.length === 0 ? (
                                        <p className="no-notifications">{ihm_viewer__dictionary.NO_NOTIFICATIONS[locale()]}</p>
                                    ) : (
                                        notifications.map(n => {
                                            let projectName = '';
                                            let senderName = 'Notification';
                                            let sTranslated = '';
                                            
                                            try {
                                                const oData = JSON.parse(n.content);
                                                projectName = oData.sProjectName || '';
                                                senderName = oData.sSender || senderName;
                                                
                                                if (oData.key) {
                                                    let keyToUse = oData.key;
                                                    if (oData.nCount == 1) {
                                                        if (keyToUse === 'PUSH_MULTIPLE_MESSAGES') keyToUse = 'PUSH_NEW_MESSAGE';
                                                        if (keyToUse === 'PUSH_MULTIPLE_FILES_SENT') keyToUse = 'PUSH_FILE_SENT';
                                                    }

                                                    if (keyToUse === 'PUSH_CHAT_MESSAGE') {
                                                        keyToUse = 'PUSH_NEW_MESSAGE';
                                                    }

                                                    if (ihm_viewer__dictionary[keyToUse]) {
                                                        sTranslated = ihm_viewer__dictionary[keyToUse][locale()];
                                                        if (oData.nCount) sTranslated = sTranslated.replace('{{count}}', oData.nCount);
                                                        if (oData.sSender) sTranslated = sTranslated.replace('{{sender}}', oData.sSender);
                                                        if (oData.sVersion) sTranslated = sTranslated.replace('{{version}}', oData.sVersion);
                                                    } else {
                                                        sTranslated = n.content;
                                                    }
                                                } else {
                                                    sTranslated = n.content;
                                                }
                                            } catch (e) {
                                                sTranslated = n.content;
                                            }

                                            return (
                                                <div key={n.id} className={`notification-item ${!n.isRead ? 'unread' : ''}`} onClick={() => markAsRead(n.id, n.url)}>
                                                    <div className="timelineRow">
                                                        <div className="avatarCol">
                                                            <img className="msgAvatar" src={`https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&background=random&color=fff&size=128`} alt="" loading="lazy" />
                                                        </div>
                                                        <div className="contentCol">
                                                            <div className="msgMeta">
                                                                <strong>{senderName}</strong>
                                                                <span className="msgDate">{new Date(n.createdAt).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                            </div>
                                                            <div className="msgBody">
                                                                {projectName ? `[${projectName}] ` : ''}
                                                                {sTranslated}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button className="delete-notif-btn" onClick={(e) => deleteNotification(e, n.id)}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            <div className="notification-footer">
                                {pushPermission === 'granted' && (
                                    <button onClick={handleToggleNotifications} className="glass-btn toggle-notif-btn-footer">
                                        {(notificationsEnabled && pushPermission === 'granted') ? (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <defs>
                                                        <mask id="bell-slash-mask-footer-mobile">
                                                            <rect x="0" y="0" width="24" height="24" fill="white" />
                                                            <line x1="3" y1="3" x2="21" y2="21" stroke="black" strokeWidth="3" strokeLinecap="round" />
                                                        </mask>
                                                    </defs>
                                                    <path mask="url(#bell-slash-mask-footer-mobile)" d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                                                    <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                                {locale() === 'fr' ? 'Désactiver' : 'Disable'}
                                            </>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                                                </svg>
                                                {locale() === 'fr' ? 'Activer' : 'Enable'}
                                            </>
                                        )}
                                    </button>
                                )}
                                {notifications.length > 0 && (
                                    <div className="footer-actions-right">
                                        {unreadCount > 0 && (
                                            <button onClick={markAllAsRead} className="glass-btn">{ihm_viewer__dictionary.READ_ALL[locale()]}</button>
                                        )}
                                        <button onClick={deleteAll} className="glass-btn">{ihm_viewer__dictionary.DELETE_ALL[locale()]}</button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            , document.body) : (
                <div className="notification-dropdown">
                    {!isAuthenticated ? (
                        <div className="notification-body">
                            <div className="notification-guest">
                                {guestInfo && guestInfo.linked ? (
                                    <p>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_1[locale()]} <strong>{guestInfo.email}</strong>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_2[locale()]} <strong>{guestInfo.unreadCount}</strong>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_LINKED_3[locale()]}
                                    </p>
                                ) : (
                                    <p>
                                        {ihm_viewer__dictionary.NOTIFICATION_GUEST_TEXT[locale()]}
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="notification-body">
                                {pushPermission !== 'granted' && (
                                    <div className="notification-push-promo">
                                        <p>
                                            {pushPermission === 'denied' 
                                                ? ihm_viewer__dictionary.NOTIFICATION_BLOCKED[locale()]
                                                : ihm_viewer__dictionary.NOTIFICATION_PROMO[locale()]}
                                        </p>
                                        {pushPermission === 'default' && (
                                            <button className="glass-btn" onClick={requestPushPermission} style={{ flexShrink: 0 }}>
                                                {ihm_viewer__dictionary.ACTIVATE[locale()]}
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div className="notification-list">
                                    {notifications.length === 0 ? (
                                        <p className="no-notifications">{ihm_viewer__dictionary.NO_NOTIFICATIONS[locale()]}</p>
                                    ) : (
                                        notifications.map(n => {
                                            let projectName = '';
                                            let senderName = 'Notification';
                                            let sTranslated = '';
                                            
                                            try {
                                                const oData = JSON.parse(n.content);
                                                projectName = oData.sProjectName || '';
                                                senderName = oData.sSender || senderName;
                                                
                                                if (oData.key) {
                                                    let keyToUse = oData.key;
                                                    if (oData.nCount == 1) {
                                                        if (keyToUse === 'PUSH_MULTIPLE_MESSAGES') keyToUse = 'PUSH_NEW_MESSAGE';
                                                        if (keyToUse === 'PUSH_MULTIPLE_FILES_SENT') keyToUse = 'PUSH_FILE_SENT';
                                                    }

                                                    if (keyToUse === 'PUSH_CHAT_MESSAGE') {
                                                        keyToUse = 'PUSH_NEW_MESSAGE';
                                                    }

                                                    if (ihm_viewer__dictionary[keyToUse]) {
                                                        sTranslated = ihm_viewer__dictionary[keyToUse][locale()];
                                                        if (oData.nCount) sTranslated = sTranslated.replace('{{count}}', oData.nCount);
                                                        if (oData.sSender) sTranslated = sTranslated.replace('{{sender}}', oData.sSender);
                                                        if (oData.sVersion) sTranslated = sTranslated.replace('{{version}}', oData.sVersion);
                                                    } else {
                                                        sTranslated = n.content;
                                                    }
                                                } else {
                                                    sTranslated = n.content;
                                                }
                                            } catch (e) {
                                                sTranslated = n.content;
                                            }

                                            return (
                                                <div key={n.id} className={`notification-item ${!n.isRead ? 'unread' : ''}`} onClick={() => markAsRead(n.id, n.url)}>
                                                    <div className="timelineRow">
                                                        <div className="avatarCol">
                                                            <img className="msgAvatar" src={`https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&background=random&color=fff&size=128`} alt="" loading="lazy" />
                                                        </div>
                                                        <div className="contentCol">
                                                            <div className="msgMeta">
                                                                <strong>{senderName}</strong>
                                                                <span className="msgDate">{new Date(n.createdAt).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                            </div>
                                                            <div className="msgBody">
                                                                {projectName ? `[${projectName}] ` : ''}
                                                                {sTranslated}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button className="delete-notif-btn" onClick={(e) => deleteNotification(e, n.id)}>
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                            <div className="notification-footer">
                                {pushPermission === 'granted' && (
                                    <button onClick={handleToggleNotifications} className="glass-btn toggle-notif-btn-footer">
                                        {(notificationsEnabled && pushPermission === 'granted') ? (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <defs>
                                                        <mask id="bell-slash-mask-footer-desktop">
                                                            <rect x="0" y="0" width="24" height="24" fill="white" />
                                                            <line x1="3" y1="3" x2="21" y2="21" stroke="black" strokeWidth="3" strokeLinecap="round" />
                                                        </mask>
                                                    </defs>
                                                    <path mask="url(#bell-slash-mask-footer-desktop)" d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                                                    <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                                {locale() === 'fr' ? 'Désactiver' : 'Disable'}
                                            </>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" />
                                                </svg>
                                                {locale() === 'fr' ? 'Activer' : 'Enable'}
                                            </>
                                        )}
                                    </button>
                                )}
                                {notifications.length > 0 && (
                                    <div className="footer-actions-right">
                                        {unreadCount > 0 && (
                                            <button onClick={markAllAsRead} className="glass-btn">{ihm_viewer__dictionary.READ_ALL[locale()]}</button>
                                        )}
                                        <button onClick={deleteAll} className="glass-btn">{ihm_viewer__dictionary.DELETE_ALL[locale()]}</button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

export default NotificationBell;
